import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { PortableText } from '@portabletext/react';
import React, { useState, useEffect } from 'react';
import { client } from '../lib/sanity';
import { urlFor } from '../lib/imageBuilder';
import { getProjectBySlug } from '../data/projects';
import { AnnotatedScreens } from './AnnotatedScreens';

// GROQ query for single project
const PROJECT_QUERY = `*[_type == "caseStudy" && slug.current == $slug][0] {
  _id,
  title,
  "slug": slug.current,
  description,
  category,
  year,
  role,
  duration,
  tools,
  thumbnail,
  heroImage,
  overview,
  challenge,
  solution,
  outcomes,
  content
}`;

// Query for next/prev navigation
const NAV_QUERY = `*[_type == "caseStudy"] | order(publishedAt desc) {
  _id,
  title,
  "slug": slug.current
}`;

// Helper function to get YouTube/Vimeo embed URL
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  return null;
};

// Helper function to get Figma embed URL
const getFigmaEmbedUrl = (url) => {
  if (!url) return null;
  return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`;
};

// Lightbox Component — shared by all enlargeable images
const ImageLightbox = ({ imageSrc, alt, isOpen, onClose }) => {
  // Close on scroll
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleScroll = () => {
      onClose();
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen, onClose]);

  // Close on escape key
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Background overlay - light with blur */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-sm" />
      
      {/* Image container - sized to fill most of the viewport */}
      <motion.div
        className="relative z-10 bg-white rounded-lg shadow-2xl p-4 md:p-6 flex items-center justify-center"
        style={{
          width: '92vw',
          maxWidth: '1600px',
          maxHeight: '92vh',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image — scales up to fill container while preserving aspect ratio */}
        <img
          src={imageSrc}
          alt={alt || 'Enlarged view'}
          className="rounded"
          style={{
            maxWidth: '100%',
            maxHeight: 'calc(92vh - 3rem)',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </motion.div>
    </motion.div>
  );
};

// Reusable enlargeable image wrapper.
// When `enlargeable` is true: adds zoom-in cursor, hover opacity, and click-to-open lightbox.
// When false/undefined: renders a plain, passive <img>.
const EnlargeableImage = ({ src, fullSrc, alt, enlargeable, className = '', imgProps = {} }) => {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  const baseClass = `w-full h-auto rounded-lg ${className}`.trim();
  const interactiveClass = enlargeable
    ? 'cursor-zoom-in hover:opacity-90 transition-opacity'
    : '';

  return (
    <>
      <img
        src={src}
        alt={alt || ''}
        className={`${baseClass} ${interactiveClass}`.trim()}
        onClick={enlargeable ? () => setLightboxOpen(true) : undefined}
        data-testid={enlargeable ? 'enlargeable-image' : 'static-image'}
        {...imgProps}
      />
      {enlargeable && (
        <ImageLightbox
          imageSrc={fullSrc || src}
          alt={alt}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

// Two Column Layout Component with Lightbox
const TwoColumnLayoutComponent = ({ value }) => {
  if (!value) return null;
  const imageOnLeft = value.imagePosition !== 'right';

  // Default to true for backward compatibility with existing two-column blocks
  const isEnlargeable = value.image?.enlargeable !== false;

  const fullSrc = value.image?.asset ? urlFor(value.image).width(2400).url() : null;
  const thumbSrc = value.image?.asset ? urlFor(value.image).width(600).url() : null;

  const imageElement = value.image?.asset ? (
    <div className="flex-1">
      <EnlargeableImage
        src={thumbSrc}
        fullSrc={fullSrc}
        alt={value.image?.alt || 'Column image'}
        enlargeable={isEnlargeable}
      />
    </div>
  ) : null;

  const textElement = (
    <div className="flex-1 flex flex-col justify-center">
      {value.text && (
        <PortableText value={value.text} components={portableTextComponents} />
      )}
    </div>
  );

  return (
    <div className="my-8 flex flex-col md:flex-row gap-8 items-center">
      {imageOnLeft ? (
        <>
          {imageElement}
          {textElement}
        </>
      ) : (
        <>
          {textElement}
          {imageElement}
        </>
      )}
    </div>
  );
};

// Slideshow Component
const ImageSlideshow = ({ slides }) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [imageHeight, setImageHeight] = React.useState(0);
  const imageRef = React.useRef(null);
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === slides?.length - 1;

  // Calculate offset: center the current slide, showing 10% of prev/next
  // Each slide is 80% width with 10% gaps on each side
  const slideWidth = 80; // percentage
  const gapWidth = 10; // percentage on each side
  const offset = currentIndex * slideWidth;

  // Update image height for arrow positioning
  React.useEffect(() => {
    const updateHeight = () => {
      if (imageRef.current) {
        setImageHeight(imageRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [currentIndex]);

  // Early return AFTER hooks
  if (!slides || slides.length === 0) return null;
  
  return (
    <div className="my-8">
      {/* Slideshow Container */}
      <div className="relative overflow-hidden">
        {/* Track that slides */}
        <motion.div
          className="flex"
          animate={{ x: `calc(-${offset}% + ${gapWidth}%)` }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ 
            marginLeft: isFirst ? '0%' : '0%',
          }}
        >
          {slides.map((slide, index) => {
            const slideEnlargeable = slide.enlargeable === true;
            const slideThumb = urlFor(slide.image).width(1200).url();
            const slideFull = urlFor(slide.image).width(2400).url();
            return (
            <div
              key={index}
              className="flex-shrink-0 px-2"
              style={{ width: `${slideWidth}%` }}
            >
              <div className="rounded-lg overflow-hidden" ref={index === currentIndex ? imageRef : null}>
                <EnlargeableImage
                  src={slideThumb}
                  fullSrc={slideFull}
                  alt={slide.image?.alt || slide.heading || slide.caption || `Slide ${index + 1}`}
                  enlargeable={slideEnlargeable}
                  imgProps={{
                    onLoad: () => {
                      if (index === currentIndex && imageRef.current) {
                        setImageHeight(imageRef.current.offsetHeight);
                      }
                    },
                  }}
                />
              </div>
              
              {/* Caption - moves with image */}
              {(slide.heading || slide.caption) && (
                <div className="text-center mt-4 px-4">
                  {slide.heading && (
                    <p className="font-body font-semibold text-foreground-primary mb-1">
                      {slide.heading}
                    </p>
                  )}
                  {slide.caption && (
                    <p className="text-sm text-foreground-secondary">
                      {slide.caption}
                    </p>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </motion.div>

        {/* Left fade gradient - only show if not first */}
        {!isFirst && slides.length > 1 && (
          <div 
            className="absolute left-0 top-0 pointer-events-none z-10"
            style={{ 
              height: imageHeight || '100%',
              width: '12%',
              background: 'linear-gradient(to right, rgba(253,253,253,1) 0%, rgba(253,253,253,0) 100%)'
            }}
          />
        )}
        
        {/* Right fade gradient - only show if not last */}
        {!isLast && slides.length > 1 && (
          <div 
            className="absolute right-0 top-0 pointer-events-none z-10"
            style={{ 
              height: imageHeight || '100%',
              width: '12%',
              background: 'linear-gradient(to left, rgba(253,253,253,1) 0%, rgba(253,253,253,0) 100%)'
            }}
          />
        )}

        {/* Navigation arrows - centered over image only */}
        {slides.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-[12%] z-20 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-colors"
              style={{ top: imageHeight ? imageHeight / 2 : '50%', transform: 'translateY(-50%)' }}
              aria-label="Previous slide"
            >
              <svg className="w-5 h-5 text-foreground-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-[12%] z-20 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md transition-colors"
              style={{ top: imageHeight ? imageHeight / 2 : '50%', transform: 'translateY(-50%)' }}
              aria-label="Next slide"
            >
              <svg className="w-5 h-5 text-foreground-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
      
      {/* Dots indicator */}
      {slides.length > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-foreground-secondary' : 'bg-border-subtle'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Portable Text components for rendering rich content
const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;
      const thumbSrc = urlFor(value).width(800).url();
      const fullSrc = urlFor(value).width(2400).url();
      const isEnlargeable = value.enlargeable === true;
      return (
        <figure className="my-8">
          <EnlargeableImage
            src={thumbSrc}
            fullSrc={fullSrc}
            alt={value.alt || 'Case study image'}
            enlargeable={isEnlargeable}
          />
          {value.caption && (
            <figcaption className="text-center text-sm text-foreground-secondary mt-2">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    videoEmbed: ({ value }) => {
      const embedUrl = getVideoEmbedUrl(value?.url);
      if (!embedUrl) return null;
      return (
        <figure className="my-8">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video embed"
            />
          </div>
          {value.caption && (
            <figcaption className="text-center text-sm text-foreground-secondary mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    figmaEmbed: ({ value }) => {
      const embedUrl = getFigmaEmbedUrl(value?.url);
      if (!embedUrl) return null;
      return (
        <figure className="my-8">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              className="absolute top-0 left-0 w-full h-full rounded-lg border border-border-subtle"
              allowFullScreen
              title="Figma embed"
            />
          </div>
          {value.caption && (
            <figcaption className="text-center text-sm text-foreground-secondary mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
    twoColumnLayout: ({ value }) => {
      return <TwoColumnLayoutComponent value={value} />;
    },
    imageSlideshow: ({ value }) => {
      return <ImageSlideshow slides={value?.slides} />;
    },
    annotatedScreens: ({ value }) => {
      return <AnnotatedScreens screens={value?.screens} />;
    },
  },
  block: {
    h2: ({ children }) => (
      <h2 className="font-heading text-2xl lg:text-3xl text-foreground-primary mt-12 mb-4">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-heading text-xl lg:text-2xl text-foreground-primary mt-8 mb-3">{children}</h3>
    ),
    normal: ({ children }) => (
      <p className="font-body text-foreground-secondary leading-relaxed mb-4">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent pl-4 my-6 italic text-foreground-secondary">{children}</blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-outside ml-6 mb-4 space-y-2 text-foreground-secondary">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-outside ml-6 mb-4 space-y-2 text-foreground-secondary">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="font-body leading-relaxed">{children}</li>
    ),
    number: ({ children }) => (
      <li className="font-body leading-relaxed">{children}</li>
    ),
  },
  marks: {
    strong: ({ children }) => <strong className="font-medium text-foreground-primary">{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ value, children }) => (
      <a href={value?.href} className="text-accent hover:text-accent-hover underline" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
};

export const ProjectPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingSanity, setUsingSanity] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchProject = async () => {
      try {
        // Try Sanity first
        const [sanityProject, sanityNav] = await Promise.all([
          client.fetch(PROJECT_QUERY, { slug }),
          client.fetch(NAV_QUERY)
        ]);
        
        if (sanityProject) {
          setProject(sanityProject);
          setAllProjects(sanityNav);
          setUsingSanity(true);
        } else {
          // Fall back to static data
          const staticProject = getProjectBySlug(slug);
          setProject(staticProject);
          const { projects } = await import('../data/projects');
          setAllProjects(projects);
        }
      } catch (error) {
        // Fall back to static data
        const staticProject = getProjectBySlug(slug);
        setProject(staticProject);
        const { projects } = await import('../data/projects');
        setAllProjects(projects);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-foreground-primary mb-4">Project not found</h1>
          <Link to="/" className="text-accent hover:text-accent-hover transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  // Find next/previous projects
  const currentIndex = allProjects.findIndex(p => p.slug === slug);
  const nextProject = allProjects[currentIndex + 1] || allProjects[0];
  const prevProject = allProjects[currentIndex - 1] || allProjects[allProjects.length - 1];

  // Handle both Sanity and static images
  const heroImageUrl = project.heroImage?.asset 
    ? urlFor(project.heroImage).width(1600).url()
    : project.heroImage;

  return (
    <main className="pt-20" data-testid="project-page">
      {/* Back button */}
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-foreground-secondary hover:text-foreground-primary transition-colors group"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
          <span className="font-body text-sm">Back to all work</span>
        </motion.button>
      </div>

      {/* Hero Image */}
      <motion.div 
        className="w-full aspect-[21/9] overflow-hidden bg-background-secondary"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <img
          src={heroImageUrl}
          alt={project.title}
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        {/* Header */}
        <motion.div 
          className="space-y-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <p className="font-body text-sm text-accent-text uppercase tracking-wider">
            {project.category} · {project.year}
          </p>
          <h1 className="font-headline text-4xl lg:text-6xl text-foreground-primary tracking-tight">
            {project.title}
          </h1>
          <p className="font-body text-xl text-foreground-secondary leading-relaxed">
            {project.description}
          </p>
        </motion.div>

        {/* Project Meta */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 py-10 border-y border-border-subtle mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div>
            <p className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2">Role</p>
            <p className="font-body text-foreground-primary">{project.role}</p>
          </div>
          <div>
            <p className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2">Duration</p>
            <p className="font-body text-foreground-primary">{project.duration}</p>
          </div>
          <div>
            <p className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2">Tools</p>
            <p className="font-body text-foreground-primary">
              {Array.isArray(project.tools) ? project.tools.join(', ') : project.tools}
            </p>
          </div>
        </motion.div>

        {/* If using Sanity with rich content */}
        {usingSanity && project.content && (
          <motion.div
            className="prose-custom"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <PortableText value={project.content} components={portableTextComponents} />
          </motion.div>
        )}

        {/* Static content sections (fallback) */}
        {!usingSanity && (
          <>
            {/* Overview */}
            <motion.section 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl lg:text-3xl text-foreground-primary mb-6">Overview</h2>
              <p className="font-body text-foreground-secondary leading-relaxed text-lg">
                {project.overview}
              </p>
            </motion.section>

            {/* Challenge */}
            <motion.section 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl lg:text-3xl text-foreground-primary mb-6">The Challenge</h2>
              <p className="font-body text-foreground-secondary leading-relaxed text-lg">
                {project.challenge}
              </p>
            </motion.section>

            {/* Solution */}
            <motion.section 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl lg:text-3xl text-foreground-primary mb-6">The Solution</h2>
              <p className="font-body text-foreground-secondary leading-relaxed text-lg">
                {project.solution}
              </p>
            </motion.section>

            {/* Outcomes */}
            {project.outcomes && (
              <motion.section 
                className="mb-20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="font-heading text-2xl lg:text-3xl text-foreground-primary mb-6">Key Outcomes</h2>
                <ul className="space-y-4">
                  {project.outcomes.map((outcome, index) => (
                    <motion.li
                      key={index}
                      className="flex items-start gap-4"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <ArrowUpRight className="w-5 h-5 text-accent mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <span className="font-body text-foreground-primary text-lg">{outcome}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.section>
            )}
          </>
        )}

        {/* Navigation to other projects */}
        <motion.div 
          className="border-t border-border-subtle pt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="grid grid-cols-2 gap-8">
            <Link 
              to={`/work/${prevProject.slug}`}
              className="group"
              data-testid="prev-project-link"
            >
              <p className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2">Previous</p>
              <p className="font-heading text-xl text-foreground-primary group-hover:text-accent transition-colors">
                {prevProject.title}
              </p>
            </Link>
            <Link 
              to={`/work/${nextProject.slug}`}
              className="group text-right"
              data-testid="next-project-link"
            >
              <p className="font-body text-xs text-foreground-secondary uppercase tracking-wider mb-2">Next</p>
              <p className="font-heading text-xl text-foreground-primary group-hover:text-accent transition-colors">
                {nextProject.title}
              </p>
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default ProjectPage;
